<!-- 事務局メッセージタブ（学校管理者向け）
     Counselor/tabs/Messages.vue と同等の実装。
     共通化する場合は Counselor 側も合わせて修正すること。 -->
<template>
  <div>
    <spacer :y="5"/>
    <loader-simple :flag="flag.loader">
      <div v-if="flag.error">
        <spacer :y="4"/>
        <p>メッセージの読み込みに失敗しました。ページをリロードしてください。</p>
      </div>

      <div v-if="!flag.loader && !flag.error">
        <contents-box title="メッセージ登録">
          <div>
            <textarea
              :class="$style.textarea"
              placeholder="内容を入力してください"
              v-model="message">
            </textarea>
          </div>
          <spacer :y="3"/>
          <btn-container>
            <basic-btn
              tag="button"
              v-on:click="submit">登録する</basic-btn>
          </btn-container>
        </contents-box>

        <spacer :y="4"/>

        <contents-box v-if="data && data.messages && data.messages.length">
          <div>
            <div :class="$style.comment" v-for="chat in data.messages" :key="chat.id">
              <stack-item
                :class="chat.user.account_type === 31 ? $style.q : $style.a">
                <div :class="[chat.user.account_type === 31 ? $style.question : $style.answer, isMessageAddressedToViewer(chat, user) ? $style.toViewer : '']">
                  <!-- 未確認メッセージに NEW マーク（学校共有ルーム＝学校管理者専用画面・isUnreadMessage で判定・#3185） -->
                  <div v-if="isUnreadMessage(chat)" :class="$style.new">NEW</div>
                  <!-- 氏名＋F情報。F情報は学校管理者(11/12)のみ氏名右にインライン表示・未設定は（要更新）・他ロールは非表示。
                       事務局が宛先(To)を付けた発言は氏名の後ろに「→ ○○さん（所属等）」、自分が宛先なら「あなた」（所属等は付けない） -->
                  <div :class="[$style.name, isInactivePostUser(chat) ? $style.inactiveName : '']">{{ getPostUserDisplayName(chat) }}<span v-if="getPostUserAffiliation(chat)" :class="$style.affiliation">{{ getPostUserAffiliation(chat) }}</span><span v-if="isInactivePostUser(chat)" :class="$style.inactiveLabel">（無効）</span><template v-if="getMessageToNames(chat, user).length"><span :class="$style.toArrow" aria-label="宛先">→</span><span :class="$style.toNames"><template v-for="(toName, ti) in getMessageToNames(chat, user)" :key="toName.key">{{ ti ? '、' : '' }}<span :class="toName.isViewer ? $style.toMe : ''">{{ toName.label }}</span><span v-if="toName.affiliation" :class="[$style.affiliation, $style.toAffiliation]">{{ toName.affiliation }}</span><span v-if="toName.inactiveLabel" :class="$style.inactiveLabel">{{ toName.inactiveLabel }}</span></template></span></template> [{{ filterMoment(chat.updated_at, 'YYYY.MM.DD HH:mm') }}]</div>
                  <spacer :y="1"/>
                  <div>
                    <p :class="$style.messageBody">{{ chat.message }}</p>
                  </div>
                </div>
              </stack-item>
            </div>
          </div>
        </contents-box>
      </div>
    </loader-simple>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ContentsBox from '@/views/components/ContentsBox.vue';
import StackItem from '@/views/components/StackItem.vue';
import Spacer from '@/views/components/Spacer.vue';
import LoaderSimple from '@/views/components/LoaderSimple.vue';
import BasicBtn from '@/views/components/BasicBtn.vue';
import BtnContainer from '@/views/components/BtnContainer.vue';
import cf from '@/mixins/commonFunctions';
import messageTo from '@/mixins/messageTo';

export default {
  name: 'Messages',
  mixins: [cf, messageTo],
  components: {
    ContentsBox,
    StackItem,
    Spacer,
    LoaderSimple,
    BasicBtn,
    BtnContainer,
  },
  // 他タブコンポーネント（Myschool等）とpropsインターフェースを揃えるために定義
  props: {
    school: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      messageGroupId: null,
      data: null,
      message: null,
      // 既読登録"前"の readflags スナップショット。
      // updateReadflags が load 時に既読化＋user/update するため、NEW 判定はこのスナップショットを基準にする（#3185）
      readSnapshot: [],
      flag: {
        loader: true,
        error: false,
      },
    };
  },
  created() {
    this.initMessageGroup();
  },
  computed: {
    ...mapState(['user', 'helper']),
  },
  methods: {
    /**
     * 未確認メッセージ判定。未読（readSnapshot 未登録）かつ自分以外の投稿。
     * 当画面は学校共有ルーム（学校管理者専用）のため account_type gate は不要。（#3185）
     */
    isUnreadMessage(chat) {
      if (!this.user.readflags || !this.user.readflags.operatorMessages) return false;
      return !this.readSnapshot.includes(chat.id) && chat.user_id !== this.user.id;
    },
    /** ローディング表示 */
    showLoading() {
      const args = { modalName: 'modalLoadingBallScaleRippleMultiple' };
      this.$store.dispatch('modal/loadings/showModal', args, { root: true });
    },
    /** ローディング非表示 */
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },

    /** メッセージグループを取得または作成し、詳細を表示 */
    async initMessageGroup() {
      // 既読登録"前"のスナップショットを取得（updateReadflags の user/update 前に確定させる・#3185）
      this.readSnapshot = this.user.readflags ? [...(this.user.readflags.operatorMessages || [])] : [];
      const operatorUserId = 1; // 事務局ユーザID（usersテーブル）
      // 事務局↔学校管理者の学校共有ルーム（target_type=4）。1 校 1 ルームに主/副管理者・事務局が集い相互閲覧（#3185 ②）。
      const schoolRoomTargetType = 4;
      // 自校 school_id（WTT は login ロール 11/12 が単一校前提＝user.school[0]）
      const schoolId = this.user.school && this.user.school.length ? this.user.school[0].id : null;
      if (!schoolId) {
        this.flag.error = true;
        this.flag.loader = false;
        return;
      }
      try {
        let response = await this.axios({
          method: 'GET',
          url: '/v1/messagegroup/get/list',
          params: {
            target_type: schoolRoomTargetType,
            group_school_id: schoolId, // messageGroups.school_id 列で自校 type=4 ルームを引く
            isOperator: 1,
            // 閲覧者 id。副管理者(12)判定と login_enabled_at 基準時刻の解決は backend resolver が
            // DB から完結する（frontend は「誰が見ているか」だけ送り、業務ルールは持たない）
            viewer_user_id: this.user.id,
          },
        });
        if (response.data.groups.data.length === 0) {
          // find-or-create: backend が (target_type=4, school_id) で既存検索 → 無ければ user_id=事務局(1) 固定で生成
          const data = {
            user_id: operatorUserId,
            target_type: schoolRoomTargetType,
            school_id: schoolId,
          };
          response = await this.axios({
            method: 'POST',
            url: '/v1/messageGroup/set/register',
            data,
          });
          this.messageGroupId = response.data.group_id;
        } else {
          this.messageGroupId = response.data.groups.data[0].id;
          await this.updateReadflags(response.data.groups.data[0]);
        }
        await this.getDetail();
      } catch (error) {
        if (error.response) console.log(error.response.data);
        else console.log(error);
        this.flag.error = true;
        this.flag.loader = false;
      }
    },

    /** メッセージ詳細を取得 */
    async getDetail() {
      try {
        const response = await this.axios({
          method: 'GET',
          url: '/v1/messagegroup/get/detail',
          params: {
            id: this.messageGroupId,
            isOperator: 1,
            // 閲覧者 id。副管理者(12)判定と login_enabled_at 基準時刻の解決は backend resolver が
            // DB から完結する（room 種別 type=4 の確定は backend の lean lookup 側で行う）
            viewer_user_id: this.user.id,
          },
        });
        this.data = response.data.detail;
        this.flag.loader = false;
      } catch (error) {
        if (error.response) console.log(error.response.data);
        else console.log(error);
        this.flag.error = true;
        this.flag.loader = false;
      }
    },

    /** メッセージ送信 */
    submit(e) {
      e.preventDefault();
      if (!this.message) {
        return alert('メッセージが入力されていません。');
      }
      const submitConfirm = confirm('返信は取り消せません。よろしいですか？');
      if (!submitConfirm) return;

      this.showLoading();

      const data = {
        group_id: this.data.id,
        messageData: {
          user_id: this.user.id,
          message: this.message,
        },
      };

      this.axios({
        method: 'POST',
        url: '/v1/messageGroup/set/register',
        data,
      })
        .then(async () => {
          alert('メッセージを登録しました');
          this.message = null;
          this.hideLoading();
          this.flag.loader = true;
          this.getDetail();
        })
        .catch((error) => {
          if (error.message) console.log(error.message);
          else console.log(error);
          this.hideLoading();
          alert('メッセージの送信に失敗しました。');
        });
    },

    /** 既読フラグを更新 */
    async updateReadflags(messageGroup) {
      const receiveMessages = messageGroup.messages.filter((msg) => msg.user_id !== this.user.id);
      const readMessageIds = this.user.readflags ? this.user.readflags.operatorMessages || [] : [];
      const unreadMessages = receiveMessages.filter((msg) => !readMessageIds.includes(msg.id));
      if (unreadMessages.length === 0) {
        return;
      }
      try {
        await Promise.all(unreadMessages.map((msg) => {
          const data = {
            model_type: 6, // メッセージグループ（read_flag_model_types）
            user_id: this.user.id,
            foreign_id: msg.id,
          };
          return this.axios({
            method: 'POST',
            url: '/v1/readflag/set/register',
            data,
          });
        }));
        await this.$store.dispatch('user/update');
      } catch (error) {
        if (error.response) console.log(error.response.data);
        else console.log(error);
      }
    },
  },
};
</script>

<style lang="scss" module>
.textarea {
  padding: 20px;
  background-color: var(--gray-sub);
  border: none;
  outline: none;
  width: 100%;
  border-radius: 8px;
  resize: vertical;
  min-height: 120px;
}

.comment {
  margin-bottom: 24px;
  &:not(:first-child) >li {
    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: -24px;
      left: 30px;
      width: 3px;
      height: 24px;
    }

    &.q {
      &::before {
        background-color: var(--blue-main);
      }
    }
    &.a {
      &::before {
        background-color: var(--orange-main);
      }
    }
  }
  .name {
    font-size: 12px;
  }
}

// 無効化(flag!==1)ユーザ投稿者の氏名行をグレー化（本文は不変・#3185）
.inactiveName {
  color: var(--gray-side);
}
.inactiveLabel {
  // 投稿者が現在無効化されていることを示す小ラベル（#3185）
  font-size: 11px;
  color: var(--gray-side);
  margin-left: 2px;
}

.affiliation {
  // 投稿者氏名の右に小フォントで F 情報を表示（UserBox.belongs 風）
  // 淡色→黒文字で視認性を上げる
  font-size: 12px;
  color: var(--black);
  @include sm-view {
    font-size: 11px;
  }
}

.messageBody {
  white-space: pre-wrap;
  word-wrap: break-word;
}

// 発言の宛先。名前の行に「→ ○○さん、△△さん」と続け、自分が宛先なら氏名の代わりに「あなた」を色付きで出す
.toArrow {
  color: var(--gray-side);
  margin: 0 6px;
}
.toNames {
  font-weight: bold;
  overflow-wrap: anywhere;
}
// 宛先の所属等は投稿者の所属等（.affiliation）と同じ字にする（.toNames の太字を打ち消す）
.toAffiliation {
  font-weight: normal;
}
.toMe {
  display: inline-block;
  background-color: var(--orange-main);
  color: #fff;
  font-size: 11px;
  line-height: 1.6;
  padding: 0 7px;
  border-radius: 4px;
}
// 自分が宛先の発言は左端に同じ色の線（一覧を流し見しても拾えるように）
.question.toViewer, .answer.toViewer {
  box-shadow: inset 4px 0 0 var(--orange-main);
}

.question, .answer {
  // NEW バッジ(.new)の絶対配置基準（#3185）
  position: relative;
  padding: 15px;
  border-radius: 8px;
}
// 未確認メッセージの NEW バッジ（Operator/tabs/Message.vue・RiskApplicationForm.vue と同形・#3185）
.new {
  position: absolute;
  top: -3px;
  right: -8px;
  z-index: 1;
  background-color: var(--pink);
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 4px;
  @include sm-view {
    padding: 3px 7px 2px;
    font-size: 11px;
    right: -4px;
  }
}

.question {
  background-color: rgba(var(--blue-main-rgb), 0.2);
}
.answer {
  background-color: rgba(var(--orange-main-rgb), .2);
}
</style>
